import React, { useState, useEffect, useRef } from 'react';

function GenericAutoCompleteInput({
  value,
  onChange,
  fetchOptions,
  placeholder = '',
  id,
  index,
  clients,
  minSearch = 2, // 최소 검색어 길이
}) {
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState(value?.name || '');
  const [showList, setShowList] = useState(false);
  const containerRef = useRef(null);

  // 옵션 로드: query 길이가 충분할 때만 호출
  useEffect(() => {
    if (!showList || (query || '').length < minSearch) {
      setOptions([]);
      return;
    }
    let active = true;
    (async () => {
      try {
        const opts = await fetchOptions(query);
        if (active) setOptions(opts);
      } catch (e) {
        console.error('fetchOptions error:', e);
      }
    })();
    return () => {
      active = false;
    };
  }, [id, fetchOptions, query, showList, minSearch]);

  // 부모 value 변경 시 query 동기화
  useEffect(() => {
    setQuery(value?.name || '');
  }, [value?.id, value?.name]);

  // 외부 클릭 시 리스트 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowList(false);
      }
    };
    if (showList) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showList]);

  // 이름 또는 ID 기반 필터링 (ID는 대소문자 구분 없이 검색)
  const filtered = options.filter((option) => {
    const q = (query || '').toLowerCase();
    const nameMatch = (option.name || '').toLowerCase().includes(q);
    const idMatch = option.id.toString().toLowerCase().includes(q);
    return nameMatch || idMatch;
  });

  // 입력 변경 핸들러
  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setShowList(true);
  };

  // 항목 선택 핸들러
  const handleSelect = (option) => {
    if (clients) {
      const dupIndex = clients.findIndex((c) => c.id === option.id);
      if (dupIndex !== -1 && dupIndex !== index) {
        alert('이미 추가한 권리사입니다.');
        return;
      }
    }
    setQuery(option.name);
    setShowList(false);
    onChange(option, index);
  };

  // Blur 시 마지막 선택값 복원
  const handleBlur = () => {
    setShowList(false);
    setQuery(value?.name || '');
  };

  return (
    <div className="relative flex-1" ref={containerRef}>
      <input
        type="text"
        value={query || ''}
        onChange={handleInputChange}
        onFocus={() => setShowList(true)}
        onBlur={handleBlur}
        className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/30"
        autoComplete="off"
        placeholder={placeholder}
      />

      {/* 옵션 리스트 */}
      {showList && (query || '').length >= minSearch && filtered.length > 0 && (
        <ul className="absolute z-10 w-full bg-[#1a2b42] border border-white/10 rounded-xl mt-1 max-h-40 overflow-auto">
          {filtered.map((option) => (
            <li
              key={option.id}
              className="px-3 py-2 hover:bg-white/5 cursor-pointer text-slate-200"
              onMouseDown={() => handleSelect(option)}>
              {option.name} ({option.id})
            </li>
          ))}
        </ul>
      )}

      {/* 일치 항목 없음 */}
      {showList &&
        (query || '').length >= minSearch &&
        filtered.length === 0 && (
          <div className="absolute z-10 w-full bg-[#1a2b42] border border-white/10 rounded-xl mt-1 px-3 py-2 text-slate-400">
            일치하는 항목이 없습니다.
          </div>
        )}

      {/* 최소 검색어 안내 */}
      {showList && (query || '').length < minSearch && (
        <div className="absolute z-10 w-full bg-[#1a2b42] border border-white/10 rounded-xl mt-1 px-3 py-2 text-slate-400">
          최소 {minSearch}자 이상 입력해 주세요.
        </div>
      )}
    </div>
  );
}

export default GenericAutoCompleteInput;
